import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Select, Spin, Tag } from 'antd';
import { UserName } from '../name_component/user/UserNameComponent';
import UserRoleTags from './UserRoleTags';
import useMountEffect from '../../hooks/useMountEffect';
import { fetchFacilityUsers } from '../../thunks/users_thunks';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { BUYER_ROLES, ROLES, SUPPLIER_ROLES } from '../roles/roles';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { withUserType } from '../withUserType';

require('./UserGroupMemberSelect.less');

// `UserName` declares its click handler as a destructuring default:
//   onClick = (e) => { e.preventDefault(); e.stopPropagation(); }
// Destructuring defaults apply when the passed value is `undefined`, so `onClick={undefined}`
// ACTIVATES that default rather than clearing it — which swallowed the click that selects an
// option. A real no-op function is required to override it.
const noopClick = () => {};

const ALL_ROLES_KEY = 'ALL';

// Multi-select of the caller's own facility users, for user group membership. Works on both
// sides: the role pills, the avatar colouring and the users endpoint all follow the session's
// userType.
//
// Sourced from /admin/{role}/facility_users_list rather than the contacts catalogue on purpose:
// the backend resolves every submitted email against the caller's own tenant and rejects
// anything outside it, so the picker must offer exactly that set. The endpoint is unpaginated,
// so the whole list is fetched once on mount and both the role pills and the type-ahead filter
// it in memory — no per-keystroke round trips.
const UserGroupMemberSelect: FC<any> = ({
	value,
	onChange,
	placeholder = 'Select users',
	fetchUsers,
	userType,
}): React.ReactElement => {
	const [users, setUsers] = useState([]);
	const [loading, setLoading] = useState(true);
	const [roleFilter, setRoleFilter] = useState(ALL_ROLES_KEY);
	const [searchText, setSearchText] = useState('');

	useMountEffect(() => {
		fetchUsers()
			.then((fetched) => setUsers(fetched || []))
			.catch(() => setUsers([]))
			.finally(() => setLoading(false));
	});

	const getEmail = useCallback(
		(user) => nullSafeGetOrElse('contact.email', user, '').toLowerCase(),
		[]
	);

	const getRoles = useCallback((user) => nullSafeGetOrElse('role', user, []), []);

	// Only offer pills for roles actually present in this tenant, in the canonical role order
	// for the current side, so the row never shows a filter that matches nobody.
	const availableRoles = useMemo(() => {
		const presentRoles = new Set();
		users.forEach((user) => getRoles(user).forEach((role) => presentRoles.add(role)));
		const roleOrder = userType === ROLE_TYPES.SUPPLIER ? SUPPLIER_ROLES : BUYER_ROLES;
		return roleOrder.filter((role) => presentRoles.has(role));
	}, [getRoles, users, userType]);

	const visibleUsers = useMemo(() => {
		const term = searchText.trim().toLowerCase();
		return users.filter((user) => {
			const matchesRole = roleFilter === ALL_ROLES_KEY || getRoles(user).indexOf(roleFilter) !== -1;
			if (!matchesRole) {
				return false;
			}
			if (!term) {
				return true;
			}
			const name = `${nullSafeGetOrElse('contact.nameGiven', user, '')} ${nullSafeGetOrElse(
				'contact.nameFamily',
				user,
				''
			)}`.toLowerCase();
			return name.indexOf(term) !== -1 || getEmail(user).indexOf(term) !== -1;
		});
	}, [getEmail, getRoles, roleFilter, searchText, users]);

	const displayName = useCallback((user) => {
		const name = `${nullSafeGetOrElse('contact.nameGiven', user, '')} ${nullSafeGetOrElse(
			'contact.nameFamily',
			user,
			''
		)}`.trim();
		return name || nullSafeGetOrElse('contact.email', user, '');
	}, []);

	// antd clears its own search input on select (`autoClearSearchValue` defaults to true), but
	// the filtering here runs off local `searchText` — so without clearing it too, the list
	// stays filtered by a term the user can no longer see. Reset it whenever the selection
	// changes and pass the value on to the Form.
	const handleChange = useCallback(
		(nextValue) => {
			setSearchText('');
			if (onChange) {
				onChange(nextValue);
			}
		},
		[onChange]
	);

	// The pills live inside the dropdown, so a click must not blur the Select and close it.
	const preventDropdownClose = useCallback((event) => event.preventDefault(), []);

	const renderRoleFilters = useCallback(
		() => (
			<div
				className="flex flex-wrap items-center gap-1.5 border-0 border-b border-solid border-grey-4 px-3 py-2"
				onMouseDown={preventDropdownClose}
			>
				<Tag.CheckableTag
					checked={roleFilter === ALL_ROLES_KEY}
					onChange={() => setRoleFilter(ALL_ROLES_KEY)}
				>
					All
				</Tag.CheckableTag>
				{availableRoles.map((role) => (
					<Tag.CheckableTag
						key={role}
						checked={roleFilter === role}
						onChange={() => setRoleFilter(role)}
					>
						{nullSafeGetOrElse(`${role}.displayName`, ROLES, role)}
					</Tag.CheckableTag>
				))}
			</div>
		),
		[availableRoles, preventDropdownClose, roleFilter]
	);

	const dropdownRender = useCallback(
		(menu) => (
			<div>
				{renderRoleFilters()}
				{loading ? (
					<div className="flex justify-center py-4">
						<Spin size="small" />
					</div>
				) : (
					menu
				)}
			</div>
		),
		[loading, renderRoleFilters]
	);

	return (
		<Select
			mode="multiple"
			value={value}
			onChange={handleChange}
			placeholder={placeholder}
			style={{ width: '100%' }}
			// Search and role filtering are applied to `visibleUsers` above, so antd must not
			// also filter the options it is handed.
			filterOption={false}
			onSearch={setSearchText}
			// Keeps the selected tags compact — without this antd would render the whole
			// avatar/name/email card inside every tag.
			optionLabelProp="label"
			dropdownClassName="userGroupMemberSelectDropdown"
			dropdownRender={dropdownRender}
			notFoundContent={loading ? null : 'No users found'}
			loading={loading}
		>
			{visibleUsers.map((user) => (
				<Select.Option key={getEmail(user)} value={getEmail(user)} label={displayName(user)}>
					<div className="flex items-center justify-between gap-4">
						<UserName
							contact={nullSafeGetOrElse('contact', user, {})}
							account={nullSafeGetOrElse('account', user, {})}
							userType={userType}
							mode="card"
							showAvatar
							allowHover={false}
							underline={false}
							details={[]}
							extraDetails={[]}
							onClick={noopClick}
						/>
						<UserRoleTags roles={getRoles(user)} />
					</div>
				</Select.Option>
			))}
		</Select>
	);
};

const mapDispatchToProps = (dispatch, ownProps) => ({
	// All facility users, not just the active ones: the backend accepts any email that
	// resolves in the tenant (invited users included, and the group's own counts track active
	// vs invited separately), so restricting the picker to active users would hide people who
	// are legitimately addable.
	fetchUsers: () => dispatch(fetchFacilityUsers(ownProps.userType, {})()),
});

export default withUserType(connect(null, mapDispatchToProps)(UserGroupMemberSelect));
